/* eslint-disable unicorn/prefer-array-some */
import { View } from "extensions/marionette";
import $ from "jquery";
import { _ } from "extensions/underscore";
import Radio from "backbone.radio";
import moment from "moment";
import sweetalert from "sweetalert";
import { toast } from "extensions/toast";

import strings from "common/strings";
import { copyToClipboard } from "common/utils";

import { DateRangePicker } from "common/components/datePicker/daterangepicker";

import { references } from "common/helpers/references";
import { pazienHistory } from "common/components/pazienHistory";

import "common/components/datePicker/datePicker.scss";

import credentialsViewTemplate from "./credentialsView.jst";
import customFieldTemplate from "./customField.jst";

import { typeaheadMixin } from "./typeaheadMixin";
import "typeahead.js/dist/typeahead.jquery";

import "common/components/typeahead.scss";

const expiredDefault = moment.utc().add(90, "days");

export const CredentialsView = View.extend({
    ...typeaheadMixin,
    className: "credentialsView fluid-container",
    template: credentialsViewTemplate,
    ui: {
        description: ".description",
        descriptionInput: ".editable input",
        descriptionReadonly: ".description .read-only",
        pickDate: ".pick-date",
        duplicate: "#duplicateCred",
        
        processor_name: "#processor_name",
        merchant_ref: "#merchant_ref",
        username: "#username",
        password: "#password",
        notes: "#notes",
        
        customFieldAdd: ".customFieldAdd",
        showSecureItemsButton: ".show-secure-items",
        hideSecureItemsButton: ".hide-secure-items",
        saveButton: ".save-button",
        resetButton: ".reset-button"
    },
    events: {
        "click .edit-description": "switchDescriptionToEditMode",
        "click .read-only": "switchDescriptionToEditMode",
        "change @ui.descriptionInput": "syncDescriptionInputEndView",
        "blur @ui.descriptionInput": "switchDescriptionToReadMode",
        "click @ui.duplicate": "duplicateCredential",
        "click .copy-to-clipboard": "copyPrevSiblingToClipboard",
        "click .remove-custom-field": "removeCustomField",
        "click .open-url": "openUrlInNewWindow",
        "click @ui.showSecureItemsButton": "showSecureItems",
        "click @ui.hideSecureItemsButton": "hideSecureItems",
        "click @ui.saveButton": "saveChanges",
        "click @ui.resetButton": "resetCredentials",
        "click .protocol-name button": "changeProtocol",
        "click .remove-credential": "removeCredential",
        "keydown input, textarea": "activateSaveButton",
        "change #expired_date": "activateSaveButton",
        "click .customFieldAdd": "addCustomField"
    },
    
    modelEvents: {
        "change:display_name": "updateTabName"
    },
    
    initialize() {
        this.fetchModelIfPartial();
    },
    
    /**
     * Get more model data if dealing with partial, i.e. model data from collection
     */
    fetchModelIfPartial() {
        if (!this.model.fetched && !this.model.isNew()) {
            this.model.fetch().then(() => {
                if (!this.isDestroyed()) {
                    this.render();
                }
            }).catch(() => {
                this.updateTabName("Error");
                this.$el.html(`<div class="alert alert-danger" role="alert">
                        There was an error fetching credential with id ${this.model.id}. Most likely it does not exist.
                    </div>`);
            });
        }
        
        // If model is new then mark it as secureItemsFetched
        if (this.model.isNew()) {
            this.model.secureItemsFetched = true;
        }
    },
    
    changeProtocol(e) {
        e.preventDefault();
        this.$(e.currentTarget).addClass("active").siblings().removeClass("active");
        this.activateSaveButton();
    },
    
    addViewModelToCollection() {
        // If original model is not part of the collection, check if there is collection and put it there
        // eslint-disable-next-line eqeqeq
        if (this.model.collection == null) {
            const collection = Radio.channel("credentials").request("credentials");
            if (collection) {
                // Remove existing model with the same ID if it's there
                collection.remove(this.model.id);
                
                // Add this model
                collection.add(this.model);
            }
        }
    },
    
    duplicateCredential(e) {
        e.preventDefault();
        this.ui.duplicate.button("loading");
        this.addViewModelToCollection();
        
        const clone = this.model.clone();
        clone.unset("_id");
        
        // Make new title
        const broken = clone.get("display_name").split("-");
        const lastItem = Number.parseInt(_.last(broken), 10);
        if (Number.isNaN(lastItem)) {
            broken.push(1);
        } else {
            // Last item is integer
            broken[broken.length - 1] = lastItem + 1;
        }
        clone.set("display_name", broken.join("-"));
        
        this.showSecureItems().then((data) => {
            clone.setSecureItems(data);
            return clone;
        }).then(() => clone.save(null, { wait: true })).then(() => {
            clone.fetched = true;
            clone.secureItemsFetched = true;
            
            if (this.model.collection) {
                this.model.collection.add(clone);
            }
    
            toast.success(`Credential ${this.model.id} has been duplicated`);
            Radio.channel("credentials").trigger("credential:show", clone.id);
            this.ui.duplicate.button("reset");
        }).catch((error) => {
            toast.error(`Error fetching secure items for credential ${this.model.id}`);
        });
    },
    
    switchDescriptionToEditMode(e) {
        this.ui.description.addClass("edit");
        this.ui.descriptionInput.focus();
    },
    
    switchDescriptionToReadMode(e) {
        this.ui.description.removeClass("edit");
    },
    
    syncDescriptionInputEndView(e) {
        this.ui.descriptionReadonly.text(this.ui.descriptionInput.val());
    },
    
    getItemToCopy(elt) {
        if (elt.hasClass("secured-field")) {
            // Field is secured. We should get items and copy the result to clipboard
            return this.model.getSecuredItems(false).then((data) => {
                if (elt[0] === this.ui.username[0]) {
                    return data.username;
                }
                
                if (elt[0] === this.ui.password[0]) {
                    return data.password;
                }
                
                if (elt[0] === this.ui.notes[0]) {
                    return data.notes;
                }
                
                if (elt.attr("data-custom-name")) {
                    return data[elt.attr("data-custom-name")];
                }
            
                throw new Error("Unable to copy item to clipboard");
            });
        }
    
        return Promise.resolve(elt.val());
    },
    
    copyPrevSiblingToClipboard(e) {
        // Get previous element and copy it's value to clipboard
        const $el = this.$(e.currentTarget);

        const clipboardHelper = (str) => {
            // Don't send empty string. Use space instead.
            if (copyToClipboard(str === "" ? " " : str) === true) {
                toast.success("Copied to clipboard!");
            } else {
                toast.warning("Something went wrong. Try again.");
            }
        };

        this.getItemToCopy($el.prev()).then((value) => {
            // Check if Clipboard API is available. If it's not available - use old method.
            if (navigator.clipboard) {
                // Try Clipboard API method, if it failed use old method.
                navigator.clipboard.writeText(value)
                    .then(() => toast.success("Copied to clipboard!"))
                    .catch(() => clipboardHelper(value));
            } else {
                clipboardHelper(value);
            }
        }).catch(() => {
            toast.error("Error copying item to clipboard.");
        });
    },
    
    openUrlInNewWindow(e) {
        const $el = this.$(e.currentTarget);
        const $prevEl = $el.prev();
        window.open($prevEl.val());
    },
    
    async addTypeaheadToProviders() {
        const providers = await this.getProvidersModel();
        
        if (providers) {
            this.ui.processor_name.typeahead(null, {
                name: "providers",
                display: providers.display,
                source: providers.source,
                templates: {
                    empty: `<div class='empty-message'>${strings.preferences.typeahead.emptyMessage}</div>`,
                    suggestion: providers.templates_suggestion
                }
            });
    
            this.ui.processor_name.on("typeahead:select", (e, obj) => this.ui.processor_name.data("tvalue", obj));
            this.ui.processor_name.on("typeahead:autocomplete", (e, obj) => this.ui.processor_name.data("tvalue", obj));
        }
    },
    
    addTypeaheadToMerchant() {
        const merchants = this.getMerchantsModel();
        
        this.ui.merchant_ref.typeahead(null, {
            name: "merchants",
            display: merchants.display[0],
            source: merchants.source,
            templates: {
                empty: `<div class='empty-message'>${strings.preferences.typeahead.emptyMessage}</div>`,
                suggestion: merchants.templates_suggestion
            }
        });
    
        this.ui.merchant_ref.on("typeahead:change", (e, label) => {
            // Check if user hasn't picked item from a list, but allow empty string
            const currentTVal = this.ui.merchant_ref.data("tvalue");
            if (label === "") {
                this.ui.merchant_ref.data("tvalue", {
                    value: "",
                    label: ""
                });
            } else if (currentTVal && label !== currentTVal.label) {
                this.ui.merchant_ref.typeahead("val", currentTVal.label);
            }
        });
        this.ui.merchant_ref.on("typeahead:select", (e, obj) => this.ui.merchant_ref.data("tvalue", obj));
        this.ui.merchant_ref.on("typeahead:autocomplete", (e, obj) => this.ui.merchant_ref.data("tvalue", obj));
    
        // If credential has a merchant_ref then set it to typeahead
        if (this.model.get("merchant_ref")) {
            const merchant_alias = references.toAlias(this.model.get("merchant_ref"), "merchant");
            this.ui.merchant_ref.typeahead("val", merchant_alias);
            this.ui.merchant_ref.data("tvalue", {
                value: this.model.get("merchant_ref"),
                label: merchant_alias
            });
        }
    },
    
    initDatePicker() {
        this.datePicker = new DateRangePicker(this.ui.pickDate, {
            autoUpdateInput: false,
            singleDatePicker: true,
            showDropdowns: true,
            drops: "up",
            startDate: this.getExpiredDate(),
            endDate: this.getExpiredDate()
        });
    
        this.ui.pickDate.data("daterangepicker", this.datePicker);
    
        this.ui.pickDate.on("apply.daterangepicker", (ev, picker) => {
            this.activateSaveButton();
            this.ui.pickDate.val(picker.startDate.format("MMMM D, YYYY"));
        });
    
        this.ui.pickDate.on("cancel.daterangepicker", (ev, picker) => {
            this.ui.pickDate.val("");
        });
    },
    
    onRender() {
        this.addTypeaheadToProviders();
        this.addTypeaheadToMerchant();
        
        this.initDatePicker();
        
        this.deactivateSaveButton();
        this.updateTabName();
        
        if (this.model.secureItemsFetched) {
            this.$(".secure-items").addClass("secure-items-fetched").find("input, textarea").prop("disabled", false);
            this.ui.showSecureItemsButton.addClass("hidden");
            this.ui.hideSecureItemsButton.removeClass("hidden");
        }
    },
    
    onBeforeDestroy() {
        this.datePicker.remove();
        this.ui.pickDate.off();
    },
    
    getCustomFields() {
        // All fields except "username, password, and notes are considered custom"
        const secure_fields = this.model.get("secure_fields");
        return _.omit(secure_fields, "username", "password", "notes");
    },
    
    getExpiredDate() {
        if (this.model.has("expired_date")) {
            return moment.utc(this.model.get("expired_date"));
        }
        
        if (this.model.isNew()) {
            return expiredDefault;
        }
        return false;
    },
    
    serializeData() {
        const data = this.model.toJSON();
        
        data.notNew = !this.model.isNew();
        data.persistDataButton = this.model.isNew() ? "Create" : "Save";
        data.resetButtonMsg = "Reset";
        data.resetButtonDisabled = "";
        if (this.model.isResetRequested()) {
            data.resetButtonMsg = "Reset requested";
            data.resetButtonDisabled = "disabled";
        }
        data.customFields = Object.entries(this.getCustomFields()).map(([name, value]) => ({ name, value }));
        data.lastModified = this.getLastModifiedData();
        
        const expired_date = this.getExpiredDate();
        if (expired_date) {
            data.expired_date = expired_date.format("MMMM D, YYYY");
        } else {
            data.expired_date = "";
        }
        
        return data;
    },
    
    getLastModifiedData() {
        const history = this.model.get("change_history");
        const lastItem = _.last(history);
        
        if (lastItem) {
            return {
                // eslint-disable-next-line sentence-case/sentence-case
                modified_on: moment.utc(lastItem.modified_on).format("ddd MMM D, YYYY"),
                modified_by: lastItem.modified_by
            };
        }
        
        return false;
    },
    
    hideSecureItems(e) {
        if (e) {
            e.preventDefault();
        }
        
        const hiddenValue = "******";
    
        this.ui.showSecureItemsButton.removeClass("hidden disabled");
        this.ui.hideSecureItemsButton.addClass("hidden");
        this.$(".secure-items").removeClass("secure-items-fetched");
    
        // Hide regular secure fields
        this.ui.username.val(hiddenValue).prop("disabled", true);
        this.ui.password.val(hiddenValue).prop("disabled", true);
        this.ui.notes.val(hiddenValue).prop("disabled", true);
    
        // Hide data in custom fields
        const customFields = this.getCustomFields();
        _.each(customFields, (value, name) => {
            this.$(`.custom-field-value[data-custom-name='${name}']`).val(hiddenValue).prop("disabled", true);
            this.$(`.custom-field-name[data-custom-name='${name}']`).prop("disabled", true);
        }, this);
    
        const fieldsWithNotPersistedData = this.$(".customField:has(.custom-field-name:not([disabled]))");
        if (fieldsWithNotPersistedData.length) {
            toast.warning(`Removed ${fieldsWithNotPersistedData.length} custom fields because you have requested to hide secure items`);
            fieldsWithNotPersistedData.remove();
        }
        
        this.model.resetSecureItems();
    },
    
    showSecureItems(e) {
        if (e) {
            e.preventDefault();
        }
        
        this.ui.showSecureItemsButton.button("loading").addClass("disabled");
        
        return this.model.getSecuredItems().then((data) => {
            this.ui.showSecureItemsButton.button("reset").removeClass("disabled").addClass("hidden");
            this.ui.hideSecureItemsButton.removeClass("hidden");
            
            this.$(".secure-items").addClass("secure-items-fetched");
            
            // Show regular secure fields
            this.ui.username.val(data.username).prop("disabled", false);
            this.ui.password.val(data.password).prop("disabled", false);
            this.ui.notes.val(data.notes).prop("disabled", false);
            
            // Show data in custom fields
            const customFields = this.getCustomFields();
            _.each(customFields, (value, name) => {
                this.$(`.custom-field-value[data-custom-name='${name}']`).val(value).prop("disabled", false);
                this.$(`.custom-field-name[data-custom-name='${name}']`).prop("disabled", false);
            }, this);
            
            return data;
        }).catch((error) => {
            this.ui.showSecureItemsButton.removeClass("disabled");
            toast.error(`Error fetching secure items for credential ${this.model.id}`);
        });
    },
    
    activateSaveButton(e) {
        this.ui.saveButton.removeClass("disabled");
    },
    
    deactivateSaveButton() {
        this.ui.saveButton.addClass("disabled");
    },
    
    activateResetButton(e) {
        this.ui.resetButton.removeClass("disabled");
    },
    
    deactivateResetButton() {
        this.ui.resetButton.addClass("disabled");
    },
    
    validateSecureItems(regular_secure_fields, custom_fields, secure_fields_merged) {
        // Check if custom field name has name collisions
        if (regular_secure_fields.length + custom_fields.length > Object.keys(secure_fields_merged).length) {
            toast.warning("Unable to save credential. Ensure that your secure fields have unique names");
            return;
        }
        
        // Check if there are any fields with empty names
        if (secure_fields_merged[""]) {
            toast.warning("Unable to save credential. Ensure that all custom fields have labels");
            return;
        }
        
        return false;
    },
    
    resetCredentials(e) {
        e.preventDefault();
        this.deactivateResetButton();
        this.ui.resetButton.button("loading");
    
        return this.model.resetCredentials().then(() => {
            toast.success("Successfully sent reset credential request.");
        }).catch((error) => {
            this.ui.saveButton.button("reset").addClass("disabled");
            let description;
            try {
                description = error.response.original;
            } catch {
                description = null;
            }
            toast.error(`<div>Error resetting credential</div>
                          <pre class="code" style="font-size: 10px">${description}</pre>`);
            
            this.ui.resetButton.button("reset");
        });
    },
    
    /* jshint ignore:start */
    saveChanges(e) {
        e.preventDefault();
        this.deactivateSaveButton();
        this.ui.saveButton.button("loading");
        
        const changedAttributes = Object.create(null);
        const secure_fields = this.model.get("secure_fields");
        const hasChanged = (prop, current) => {
            if (_.isArray(prop)) {
                return _.some(prop, (el) => hasChanged(...el));
            }
            
            return current !== this.model.get(prop);
        };
        const hasSecureFieldChanged = (name, value) => {
            if (!this.model.secureItemsFetched) { return false; }
            if (_.isArray(name)) {
                return _.some(secure_fields, (val, key) => !_.find(name, (n) => n[0] === key)) || _.some(name, (el) => hasSecureFieldChanged(...el));
            }
            // eslint-disable-next-line eqeqeq
            return secure_fields[name] == null || secure_fields[name] !== value;
        };
        
        const display_name = this.$(".credential-name").val();
        if (hasChanged("display_name", display_name)) {
            changedAttributes.display_name = display_name;
        }
        
        const protocol = this.$(".protocol-name .active").attr("data-type") || null;
        if (hasChanged("protocol", protocol)) {
            changedAttributes.protocol = protocol;
        }
        
        const processor_name = this.ui.processor_name.data("tvalue") ? this.ui.processor_name.data("tvalue").value : this.ui.processor_name.val() || null;
        if (hasChanged("processor_name", processor_name)) {
            changedAttributes.processor_name = processor_name;
        }
        
        const merchant_ref = this.ui.merchant_ref.data("tvalue") ? this.ui.merchant_ref.data("tvalue").value || null : null;
        if (hasChanged("merchant_ref", merchant_ref)) {
            changedAttributes.merchant_ref = merchant_ref;
        }
        
        const url = this.$("#url").val();
        if (hasChanged("url", url)) {
            changedAttributes.url = url;
        }
    
        let expired_date;
        if (this.ui.pickDate.val() === "") {
            expired_date = null;
        } else if (this.ui.pickDate.data("daterangepicker")) {
            expired_date = this.ui.pickDate.data("daterangepicker").startDate.utc().toISOString();
        }
        
        if (hasChanged("expired_date", expired_date)) {
            changedAttributes.expired_date = expired_date;
        }
        
        // Check if any security fields have been changed
        const regular_secure_fields = [
            ["username", this.ui.username.val()],
            ["password", this.ui.password.val()],
            ["notes", this.ui.notes.val()]
        ];
        const custom_fields = $.makeArray(this.$(".customField")).map((el, index) => [$(el).find(".custom-field-name").val(), $(el).find(".custom-field-value").val()]).filter((el) => el[0] !== "");
        const secure_fields_merged = {
            ..._.object(custom_fields),
            ..._.object(regular_secure_fields)
        };
        
        if (this.validateSecureItems(regular_secure_fields, custom_fields, secure_fields_merged)) {
            // Didn't pass validation
            return false;
        }
        
        if (hasSecureFieldChanged([...regular_secure_fields, ...custom_fields])) {
            // We send all items on PATCH, even if only one has changed
            // Merge both regular fields and custom fields
            changedAttributes.secure_fields = secure_fields_merged;
        }
        
        const isNewModel = this.model.isNew();
        return this.model.updateModelWithChangedAttributes(changedAttributes).then(() => {
            this.ui.saveButton.button("reset");
            this.ui.saveButton.addClass("disabled");
            if (isNewModel) {
                // If dealing with new model then navigate to new ID, add model to the collection, and re-render the view to
                // reflect new things
                
                pazienHistory.navigateWithParams(`administrator/credentials/${this.model.id}`, {
                    replace: true
                });
                this.addViewModelToCollection();
                toast.success("Successfully created credential");
    
                // Re-render the view
                this.render();
            } else {
                toast.success("Successfully updated credential");
            }
        }).catch((error) => {
            this.ui.saveButton.button("reset").addClass("disabled");
            const { description: stackTrace } = JSON.parse(error.response?.original || {});
            
            toast.error(
                <div>
                    <div>{`Error ${isNewModel ? "creating" : "updating"} credential`}</div>
                    {stackTrace && <pre className="code" style={{ fontSize: 10 }}>{stackTrace}</pre>}
                </div>
            );
        });
    },
    /* jshint ignore:end */
    
    updateTabName(name) {
        this.triggerMethod("tab:change:name", this, (tab) => {
            tab.set("name", this.model.getTitle());
        });
    },
    
    removeCredential(e) {
        e.preventDefault();
        let title;
        let confirmButtonText;
        let message;
        if (this.model.isNew()) {
            title = "Are you sure you want to cancel creating credential?";
            confirmButtonText = "Cancel";
            message = "Canceled credential creation";
        } else {
            title = "Are you sure you want to remove this credential?";
            confirmButtonText = "Remove";
            message = "Credential has been removed";
        }
        
        sweetalert({
            title,
            text: this.model.get("display_name"),
            icon: "warning",
            buttons: {
                confirm: {
                    text: confirmButtonText,
                    className: "btn-confirm"
                },
                cancel: {
                    visible: true
                }
            }
        }).then((confirmed) => {
            if (confirmed) {
                const success = () => {
                    toast.success(message);
                    this.triggerMethod("tab:close", this);
                };
    
                if (this.model.isNew()) {
                    success();
                } else {
                    this.model.destroy({
                        wait: true,
                        success,
                        error: () => {
                            toast.error(`Error removing credential id: ${this.model.id}`);
                        }
                    });
                }
            }
        });
    },
    
    addCustomField() {
        if (this.model.secureItemsFetched) {
            const $customFieldEl = $(customFieldTemplate());
            
            $customFieldEl.insertBefore(this.ui.customFieldAdd);
            $customFieldEl.find(".custom-field-name").focus();
            this.activateSaveButton();
        }
    },
    
    removeCustomField(e) {
        e.preventDefault();
        $(e.currentTarget).closest(".form-group.customField").remove();
        this.activateSaveButton();
    }
});
